/**
 * Fuzzy string matching utilities for amino acid name spelling tolerance
 */

/**
 * Calculate Levenshtein distance between two strings
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} - Edit distance between strings
 */
function levenshteinDistance(str1, str2) {
  const matrix = [];
  
  // Create matrix
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }
  
  // Fill matrix
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }
  
  return matrix[str2.length][str1.length];
}

/**
 * Calculate similarity percentage between two strings
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} - Similarity percentage (0-100)
 */
function calculateSimilarity(str1, str2) {
  const maxLength = Math.max(str1.length, str2.length);
  if (maxLength === 0) return 100;
  
  const distance = levenshteinDistance(str1.toLowerCase(), str2.toLowerCase());
  return ((maxLength - distance) / maxLength) * 100;
}

/**
 * Check if user answer is close enough to correct answer to warrant a spelling check
 * @param {string} userAnswer - User's input
 * @param {string} correctAnswer - Correct answer
 * @param {number} threshold - Similarity threshold (default 70%)
 * @returns {object} - Result object with isClose, similarity, and suggestion
 */
export function checkSpellingTolerance(userAnswer, correctAnswer, threshold = 70) {
  if (!userAnswer || !correctAnswer) {
    return { isClose: false, similarity: 0, suggestion: null };
  }
  
  // Normalize strings
  const normalizedUser = userAnswer.trim().toLowerCase();
  const normalizedCorrect = correctAnswer.trim().toLowerCase();
  
  // Exact match
  if (normalizedUser === normalizedCorrect) {
    return { isClose: true, similarity: 100, suggestion: null, isExact: true };
  }
  
  // Calculate similarity
  const similarity = calculateSimilarity(normalizedUser, normalizedCorrect);
  
  // Check if it's close enough
  const isClose = similarity >= threshold;
  
  // Additional checks for common amino acid spelling patterns
  const isClosePattern = checkAminoAcidPatterns(normalizedUser, normalizedCorrect);
  
  return {
    isClose: isClose || isClosePattern,
    similarity: Math.round(similarity),
    suggestion: isClose || isClosePattern ? correctAnswer : null,
    isExact: false
  };
}

/**
 * Check for common amino acid spelling patterns and variations
 * @param {string} userAnswer - User's normalized answer
 * @param {string} correctAnswer - Correct normalized answer
 * @returns {boolean} - Whether it matches common patterns
 */
function checkAminoAcidPatterns(userAnswer, correctAnswer) {
  // Common amino acid misspellings and variations
  const patterns = [
    // Handle common letter swaps
    { pattern: /ph/g, replacement: 'f' },
    { pattern: /f/g, replacement: 'ph' },
    { pattern: /y/g, replacement: 'i' },
    { pattern: /i/g, replacement: 'y' },
    { pattern: /c/g, replacement: 'k' },
    { pattern: /k/g, replacement: 'c' },
    
    // Handle double letters
    { pattern: /ll/g, replacement: 'l' },
    { pattern: /nn/g, replacement: 'n' },
    { pattern: /ss/g, replacement: 's' },
    { pattern: /tt/g, replacement: 't' },
  ];
  
  // Try applying patterns to user answer
  let modifiedUser = userAnswer;
  for (const { pattern, replacement } of patterns) {
    modifiedUser = modifiedUser.replace(pattern, replacement);
    if (modifiedUser === correctAnswer) return true;
  }
  
  // Try applying patterns to correct answer
  let modifiedCorrect = correctAnswer;
  for (const { pattern, replacement } of patterns) {
    modifiedCorrect = modifiedCorrect.replace(pattern, replacement);
    if (userAnswer === modifiedCorrect) return true;
  }
  
  // Check for missing or extra spaces/hyphens
  const userNoSpaces = userAnswer.replace(/[\s-]/g, '');
  const correctNoSpaces = correctAnswer.replace(/[\s-]/g, '');
  
  if (userNoSpaces === correctNoSpaces) return true;
  
  // Check similarity without spaces
  const similarityNoSpaces = calculateSimilarity(userNoSpaces, correctNoSpaces);
  return similarityNoSpaces >= 85; // Higher threshold for pattern matching
}

/**
 * Get spelling suggestions for amino acid names
 * @param {string} userAnswer - User's input
 * @param {Array} aminoAcidList - List of valid amino acid names
 * @returns {Array} - Array of suggestions sorted by similarity
 */
export function getSpellingSuggestions(userAnswer, aminoAcidList) {
  if (!userAnswer || !aminoAcidList || aminoAcidList.length === 0) {
    return [];
  }
  
  const suggestions = aminoAcidList
    .map(aminoAcid => {
      const result = checkSpellingTolerance(userAnswer, aminoAcid, 50); // Lower threshold for suggestions
      return {
        name: aminoAcid,
        similarity: result.similarity,
        isClose: result.isClose
      };
    })
    .filter(suggestion => suggestion.similarity > 30) // Only show reasonable suggestions
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, 3); // Top 3 suggestions
  
  return suggestions;
}
